import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import {
    fetchPaymentPlan,
    savePaymentPlan,
    deletePaymentPlan,
    markInstallmentPaid,
    paymentPlanErrorKey,
} from '@/services/payment-plan.service';
import { SavePaymentPlanPayload } from '@/lib/builder/models/payment-plan.model';
import { PAYMENTS_DASHBOARD_QUERY_KEY } from '@/lib/payments/models/payments-dashboard.model';
import { PAYMENTS_FILTER_OPTIONS_QUERY_KEY } from '@/lib/payments/models/payments-filters.model';

export const PAYMENT_PLAN_QUERY_KEY = 'payment-plan';

const paymentPlanQueryKey = (itineraryId?: string) => [PAYMENT_PLAN_QUERY_KEY, itineraryId ?? null];

/**
 * Piano rate di un itinerario. Resta disabilitata finche' l'itinerario non ha un id:
 * un itinerario mai salvato non ha nulla a cui agganciare le rate.
 */
export const usePaymentPlanQuery = (itineraryId?: string) =>
    useQuery({
        queryKey: paymentPlanQueryKey(itineraryId),
        queryFn: () => fetchPaymentPlan(itineraryId as string),
        enabled: Boolean(itineraryId),
    });

export const useSavePaymentPlan = () => {
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const { t } = useI18n();

    return useMutation({
        mutationFn: (payload: SavePaymentPlanPayload) => savePaymentPlan(payload),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: paymentPlanQueryKey(variables.itinerary_id) });
            // Il piano si modifica anche dalla dashboard /payments, che aggrega importi e
            // scadenze di tutti i viaggi: dopo un salvataggio i suoi totali sono vecchi.
            queryClient.invalidateQueries({ queryKey: [PAYMENTS_DASHBOARD_QUERY_KEY] });
            // Un piano nuovo fa entrare l'itinerario (e il suo cliente) fra i valori
            // filtrabili: senza questo comparirebbe nelle tendine solo allo scadere della
            // cache, o dopo un refresh manuale.
            queryClient.invalidateQueries({ queryKey: [PAYMENTS_FILTER_OPTIONS_QUERY_KEY] });
            toast({ title: t('checkout.paymentPlan.saved') });
        },
        onError: (error) => {
            toast({ title: t(paymentPlanErrorKey(error)), variant: 'destructive' });
        },
    });
};

export const useDeletePaymentPlan = () => {
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const { t } = useI18n();

    return useMutation({
        mutationFn: (itineraryId: string) => deletePaymentPlan(itineraryId),
        onSuccess: (_data, itineraryId) => {
            queryClient.invalidateQueries({ queryKey: paymentPlanQueryKey(itineraryId) });
            // Simmetrico al salvataggio: senza piano l'itinerario esce dalla dashboard e
            // dalle tendine, e finora restava a schermo finche' non si ricaricava la pagina.
            queryClient.invalidateQueries({ queryKey: [PAYMENTS_DASHBOARD_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [PAYMENTS_FILTER_OPTIONS_QUERY_KEY] });
            toast({ title: t('checkout.paymentPlan.deleted') });
        },
        onError: (error) => {
            toast({ title: t(paymentPlanErrorKey(error)), variant: 'destructive' });
        },
    });
};

/** Incasso registrato a mano dal TD o dall'agenzia (bonifico). */
export const useMarkInstallmentPaid = (itineraryId?: string) => {
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const { t } = useI18n();

    return useMutation({
        mutationFn: (params: { installmentId: string; paidAmount?: number; paymentMethod?: string }) =>
            markInstallmentPaid(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: paymentPlanQueryKey(itineraryId) });
            // La dashboard /payments aggrega le stesse rate: senza questa invalida
            // continuerebbe a mostrare come da incassare una rata appena registrata qui.
            queryClient.invalidateQueries({ queryKey: [PAYMENTS_DASHBOARD_QUERY_KEY] });
            toast({ title: t('checkout.paymentPlan.markedPaid') });
        },
        onError: (error) => {
            toast({ title: t(paymentPlanErrorKey(error)), variant: 'destructive' });
        },
    });
};
